(() => {
  const SELECTORS = {
    drawerToggle: '[data-km-drawer-toggle]',
    drawerClose: '[data-km-drawer-close]',
    carousel: '[data-km-carousel]',
    starReviews: '[data-km-star-reviews]',
    productMediaCarousel: '[data-km-product-media-carousel]',
    productForm: '[data-km-product-form]',
    copyCode: '[data-km-copy-code]',
    quantity: '[data-km-quantity]',
    mobileBuyBar: '[data-km-mobile-buy-bar]',
    routineMedia: '.km-routine-media',
    videoReviewMedia: '.km-video-review-card__media',
    cinematicPlayer: '[data-km-cinematic-player]',
    concernResults: '[data-km-concern-results]',
    localization: '[data-km-localization]'
  };

  function formatMoney(cents, moneyFormat, currency) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && moneyFormat) {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(cents / 100);
    } catch (_error) {
      return (cents / 100).toFixed(2);
    }
  }

  function parseDiscountCampaigns(text) {
    if (Array.isArray(text)) {
      return text.map((item) => {
        const code = String(item?.code || '').trim();
        const percent = Math.max(Math.min(parseFloat(item?.percent || item?.percentage || '0') || 0, 100), 0);
        return {
          code,
          percent,
          title: item?.title || (percent ? `${percent}% OFF` : ''),
          subtext: item?.subtext || (code ? `Use code ${code}` : ''),
          deadline: item?.deadline || '',
        };
      }).filter((campaign) => campaign.code);
    }

    if (text && typeof text === 'object') {
      return parseDiscountCampaigns(text.campaigns || text.codes || []);
    }

    return String(text || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split('|').map((part) => part.trim());
        const code = parts[0] || '';
        const percent = Math.max(Math.min(parseFloat(parts[1] || '0') || 0, 100), 0);
        return {
          code,
          percent,
          title: parts[2] || (percent ? `${percent}% OFF` : ''),
          subtext: parts[3] || (code ? `Use code ${code}` : ''),
          deadline: parts[4] || '',
        };
      })
      .filter((campaign) => campaign.code);
  }

  function getUrlDiscountCode() {
    try {
      const params = new URLSearchParams(window.location.search);
      return (params.get('km_code') || '').trim();
    } catch (_error) {
      return '';
    }
  }

  function getCookieValue(name) {
    try {
      const cookie = document.cookie
        .split(';')
        .map((item) => item.trim())
        .find((item) => item.startsWith(`${name}=`));
      return cookie ? decodeURIComponent(cookie.split('=').slice(1).join('=')) : '';
    } catch (_error) {
      return '';
    }
  }

  function getShopifyDiscountCode() {
    return getCookieValue('discount_code').trim();
  }

  function getUrlAppliedDiscountCode() {
    try {
      const params = new URLSearchParams(window.location.search);
      return (params.get('km_discount_applied') || '').trim();
    } catch (_error) {
      return '';
    }
  }

  function applyDiscountSession(campaign) {
    if (!campaign?.code || window.Shopify?.designMode) return;
    const requestedCode = getUrlDiscountCode();
    if (!requestedCode || requestedCode.toLowerCase() !== campaign.code.toLowerCase()) return;
    if (getUrlAppliedDiscountCode().toLowerCase() === campaign.code.toLowerCase()) return;

    try {
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('km_code', campaign.code);
      nextUrl.searchParams.set('km_discount_applied', campaign.code);
      const redirect = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      window.location.replace(`/discount/${encodeURIComponent(campaign.code)}?redirect=${encodeURIComponent(redirect)}`);
    } catch (_error) {}
  }

  function setupDrawer(root) {
    root.querySelectorAll(SELECTORS.drawerToggle).forEach((toggle) => {
      if (toggle.dataset.kmReady === 'true') return;
      toggle.dataset.kmReady = 'true';

      const drawer = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!drawer) return;

      const closeButtons = drawer.querySelectorAll(SELECTORS.drawerClose);
      const focusableSelector = 'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled])';
      const firstFocusable = drawer.querySelector(focusableSelector);

      const close = () => {
        drawer.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('km-drawer-open');
        toggle.focus();
      };

      const open = () => {
        drawer.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('km-drawer-open');
        window.requestAnimationFrame(() => firstFocusable?.focus());
      };

      toggle.addEventListener('click', () => {
        if (drawer.hidden) open();
        else close();
      });

      closeButtons.forEach((button) => button.addEventListener('click', close));
      drawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
        if (event.key !== 'Tab') return;

        const focusable = Array.from(drawer.querySelectorAll(focusableSelector)).filter((element) => !element.hidden);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });
  }

  function setupCarousels(root) {
    root.querySelectorAll(SELECTORS.carousel).forEach((carousel) => {
      if (carousel.dataset.kmReady === 'true') return;
      carousel.dataset.kmReady = 'true';
      const rail = carousel.querySelector('[data-km-carousel-rail]');
      if (!rail) return;
      const getItems = () => Array.from(rail.children).filter((item) => item instanceof HTMLElement);
      const getActiveIndex = () => {
        const items = getItems();
        if (!items.length) return 0;
        const railCenter = rail.scrollLeft + rail.clientWidth / 2;
        return items.reduce((bestIndex, item, index) => {
          const current = items[bestIndex];
          const itemCenter = item.offsetLeft + item.offsetWidth / 2;
          const currentCenter = current.offsetLeft + current.offsetWidth / 2;
          return Math.abs(itemCenter - railCenter) < Math.abs(currentCenter - railCenter) ? index : bestIndex;
        }, 0);
      };
      const scrollToItem = (index) => {
        const items = getItems();
        if (!items.length) return;
        const nextIndex = Math.min(Math.max(index, 0), items.length - 1);
        const item = items[nextIndex];
        rail.scrollTo({
          left: item.offsetLeft - (rail.clientWidth - item.offsetWidth) / 2,
          behavior: 'smooth',
        });
      };

      carousel.querySelectorAll('[data-km-carousel-direction]').forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.dataset.kmCarouselDirection === 'previous' ? -1 : 1;
          scrollToItem(getActiveIndex() + direction);
        });
      });
    });
  }

  function parseCsv(text) {
    const rows = [];
    let row = [];
    let field = '';
    let quoted = false;

    for (let index = 0; index < text.length; index += 1) {
      const character = text[index];
      const next = text[index + 1];

      if (character === '"') {
        if (quoted && next === '"') {
          field += '"';
          index += 1;
        } else {
          quoted = !quoted;
        }
      } else if (character === ',' && !quoted) {
        row.push(field);
        field = '';
      } else if ((character === '\n' || character === '\r') && !quoted) {
        if (character === '\r' && next === '\n') index += 1;
        row.push(field);
        if (row.some((cell) => cell.trim() !== '')) rows.push(row);
        row = [];
        field = '';
      } else {
        field += character;
      }
    }

    row.push(field);
    if (row.some((cell) => cell.trim() !== '')) rows.push(row);
    return rows;
  }

  function getCsvReviews(text) {
    const rows = parseCsv(text);
    if (!rows.length) return [];

    const normalizeKey = (value) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const header = rows[0].map(normalizeKey);
    const hasHeader = header.includes('name') && (header.includes('text') || header.includes('review') || header.includes('body') || header.includes('quote'));
    const dataRows = hasHeader ? rows.slice(1) : rows;

    return dataRows.map((cells) => {
      const item = {};

      if (hasHeader) {
        header.forEach((key, index) => {
          item[key] = (cells[index] || '').trim();
        });
      } else {
        item.name = (cells[0] || '').trim();
        item.text = (cells[1] || '').trim();
        item.rating = (cells[2] || '').trim();
      }

      const name = item.name || item.customer || item.customer_name || item.author || '';
      const textValue = item.text || item.review || item.body || item.quote || item.caption || '';
      const ratingValue = parseInt(item.rating || item.stars || item.star_rating || '5', 10);
      const rating = Math.min(Math.max(Number.isFinite(ratingValue) ? ratingValue : 5, 1), 5);

      return { name, text: textValue, rating };
    }).filter((review) => review.name && review.text);
  }

  function createStarReviewCard(review) {
    const article = document.createElement('article');
    article.className = 'km-star-review-card';

    const top = document.createElement('div');
    top.className = 'km-star-review-card__top';

    const stars = document.createElement('span');
    stars.className = 'km-star-review-card__stars';
    stars.setAttribute('aria-label', `${review.rating} out of 5`);
    stars.textContent = `${'\u2605'.repeat(review.rating)}${'\u2606'.repeat(5 - review.rating)}`;

    const menu = document.createElement('span');
    menu.className = 'km-star-review-card__menu';
    menu.setAttribute('aria-hidden', 'true');
    for (let index = 0; index < 3; index += 1) menu.append(document.createElement('span'));

    const heading = document.createElement('h3');
    heading.textContent = review.name;

    const text = document.createElement('p');
    text.textContent = review.text;

    top.append(stars, menu);
    article.append(top, heading, text);
    return article;
  }

  function renderStarReviewDots(section, count) {
    const dots = section.querySelector('[data-km-star-reviews-dots]');
    if (!dots) return;
    dots.replaceChildren();

    const dotCount = Math.min(count, 5);
    for (let index = 0; index < dotCount; index += 1) {
      const dot = document.createElement('span');
      if (index === 0) dot.className = 'is-active';
      dots.append(dot);
    }
  }

  function setupStarReviews(root) {
    root.querySelectorAll(SELECTORS.starReviews).forEach((section) => {
      if (section.dataset.kmCsvReady === 'true') return;
      if (section.dataset.kmReviewsSource !== 'csv') return;

      const rail = section.querySelector('[data-km-star-reviews-rail]');
      const csvUrl = (section.dataset.kmReviewsCsvUrl || '').trim();
      if (!rail || !csvUrl) return;

      section.dataset.kmCsvReady = 'true';

      fetch(csvUrl, { cache: 'no-store' })
        .then((response) => {
          if (!response.ok) throw new Error('CSV unavailable');
          return response.text();
        })
        .then((text) => {
          const reviews = getCsvReviews(text);
          if (!reviews.length) return;
          rail.replaceChildren(...reviews.map(createStarReviewCard));
          renderStarReviewDots(section, reviews.length);
        })
        .catch(() => {
          section.dataset.kmCsvReady = 'error';
        });
    });
  }

  function setupProductMediaCarousels(root) {
    root.querySelectorAll(SELECTORS.productMediaCarousel).forEach((carousel) => {
      if (carousel.dataset.kmReady === 'true') return;
      carousel.dataset.kmReady = 'true';

      const viewport = carousel.querySelector('.km-product-media-carousel__viewport');
      const track = carousel.querySelector('[data-km-product-media-track]');
      const slides = Array.from(carousel.querySelectorAll('[data-km-product-media-slide]'));
      const dots = Array.from(carousel.querySelectorAll('[data-km-product-media-dot]'));
      if (!viewport || !track || slides.length <= 1) return;

      let activeIndex = 0;
      let pointerId = null;
      let startX = 0;
      let startScroll = 0;
      let isPointerDown = false;
      let isDragging = false;
      let suppressClick = false;
      let scrollFrame = null;
      let scrollEndTimer = null;

      const releasePointer = () => {
        if (pointerId !== null && viewport.releasePointerCapture) {
          try {
            viewport.releasePointerCapture(pointerId);
          } catch (error) {
            // The pointer may already be released by the browser.
          }
        }
      };

      const getSlideWidth = () => viewport.clientWidth || carousel.clientWidth || 1;

      const updateActiveState = (index) => {
        activeIndex = (index + slides.length) % slides.length;

        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-active', slideIndex === activeIndex);
          slide.querySelectorAll('video').forEach((video) => {
            if (slideIndex !== activeIndex) video.pause();
          });
        });

        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === activeIndex);
        });
      };

      const setActiveSlide = (index, behavior = 'smooth') => {
        const nextIndex = (index + slides.length) % slides.length;
        updateActiveState(nextIndex);
        viewport.scrollTo({
          left: nextIndex * getSlideWidth(),
          behavior,
        });
      };

      const syncFromScroll = () => {
        scrollFrame = null;
        updateActiveState(Math.round(viewport.scrollLeft / getSlideWidth()));
      };

      const settleFromScroll = () => {
        if (isPointerDown || isDragging) return;
        setActiveSlide(Math.round(viewport.scrollLeft / getSlideWidth()));
      };

      carousel.querySelectorAll('[data-km-product-media-direction]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const direction = button.dataset.kmProductMediaDirection === 'previous' ? -1 : 1;
          setActiveSlide(activeIndex + direction);
        });
        button.addEventListener('dblclick', (event) => event.preventDefault());
      });

      dots.forEach((dot, dotIndex) => {
        dot.addEventListener('click', () => setActiveSlide(dotIndex));
      });

      const finishDrag = () => {
        if (!isPointerDown && !isDragging) return;

        const dragged = isDragging;
        carousel.classList.remove('is-dragging');

        if (dragged) setActiveSlide(Math.round(viewport.scrollLeft / getSlideWidth()));

        isPointerDown = false;
        isDragging = false;
        releasePointer();
        pointerId = null;

        if (dragged) {
          window.setTimeout(() => {
            suppressClick = false;
          }, 250);
        }
      };

      viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch') return;
        if (event.button !== undefined && event.button !== 0) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = viewport.scrollLeft;
        isPointerDown = true;
        isDragging = false;
        suppressClick = false;
        try {
          viewport.setPointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture can fail when the browser has already cancelled the gesture.
        }
      });

      viewport.addEventListener('pointermove', (event) => {
        if (!isPointerDown || event.pointerType === 'touch') return;
        if (pointerId !== null && event.pointerId !== pointerId) return;

        const dx = event.clientX - startX;
        if (Math.abs(dx) > 4) {
          isDragging = true;
          suppressClick = true;
          carousel.classList.add('is-dragging');
        }

        if (!isDragging) return;
        event.preventDefault();
        viewport.scrollLeft = startScroll - dx;
      }, { passive: false });

      viewport.addEventListener('pointerup', finishDrag);
      viewport.addEventListener('pointercancel', finishDrag);
      viewport.addEventListener('lostpointercapture', finishDrag);

      window.addEventListener('pointerup', (event) => {
        if (!isPointerDown) return;
        if (pointerId !== null && event.pointerId !== pointerId) return;
        finishDrag();
      });

      window.addEventListener('pointercancel', (event) => {
        if (!isPointerDown) return;
        if (pointerId !== null && event.pointerId !== pointerId) return;
        finishDrag();
      });

      window.addEventListener('mouseleave', () => {
        if (!isPointerDown) return;
        finishDrag();
      });

      carousel.addEventListener('click', (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      }, true);

      viewport.addEventListener('scroll', () => {
        window.clearTimeout(scrollEndTimer);
        scrollEndTimer = window.setTimeout(settleFromScroll, 120);
        if (scrollFrame) return;
        scrollFrame = window.requestAnimationFrame(syncFromScroll);
      }, { passive: true });

      window.addEventListener('resize', () => setActiveSlide(activeIndex, 'auto'));

      updateActiveState(0);
      viewport.scrollLeft = 0;
    });
  }

  function setupFaq(root) {
    root.querySelectorAll('[data-km-faq-list]').forEach((list) => {
      if (list.dataset.kmReady === 'true') return;
      list.dataset.kmReady = 'true';
      if (list.dataset.singleOpen !== 'true') return;

      list.querySelectorAll('details').forEach((details) => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          list.querySelectorAll('details[open]').forEach((other) => {
            if (other !== details) other.open = false;
          });
        });
      });
    });
  }

  function setupQuantity(root) {
    root.querySelectorAll(SELECTORS.quantity).forEach((control) => {
      if (control.dataset.kmReady === 'true') return;
      control.dataset.kmReady = 'true';
      const input = control.querySelector('input[type="number"]');
      if (!input) return;

      control.querySelectorAll('button[data-step]').forEach((button) => {
        button.addEventListener('click', () => {
          if (button.dataset.step === 'up') input.stepUp();
          else input.stepDown();
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  function setupMobileBuyBars(root) {
    root.querySelectorAll(SELECTORS.mobileBuyBar).forEach((bar) => {
      if (bar.dataset.kmReady === 'true') return;
      bar.dataset.kmReady = 'true';

      const form = document.getElementById(bar.dataset.targetForm || '');
      const quantityInput = form?.querySelector('input[name="quantity"]');
      const submit = form?.querySelector('[data-km-add-to-cart]');
      const value = bar.querySelector('[data-km-mobile-quantity-value]');
      const stickySubmit = bar.querySelector('[data-km-mobile-submit]');
      if (!form || !quantityInput || !submit || !value || !stickySubmit) return;

      const sync = () => {
        value.textContent = quantityInput.value || '1';
        stickySubmit.textContent = submit.textContent.trim();
        stickySubmit.disabled = submit.disabled;
      };

      bar.querySelectorAll('[data-km-mobile-step]').forEach((button) => {
        button.addEventListener('click', () => {
          if (button.dataset.kmMobileStep === 'up') quantityInput.stepUp();
          else quantityInput.stepDown();
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          sync();
        });
      });

      stickySubmit.addEventListener('click', () => {
        submit.click();
      });

      quantityInput.addEventListener('change', sync);
      sync();
    });
  }

  function setupProductForms(root) {
    root.querySelectorAll(SELECTORS.productForm).forEach((form) => {
      if (form.dataset.kmReady === 'true') return;
      form.dataset.kmReady = 'true';

      const json = form.querySelector('[data-km-product-json]');
      const idInput = form.querySelector('[data-km-variant-id]');
      const price = form.closest('[data-km-product]')?.querySelector('[data-km-price]');
      const compare = form.closest('[data-km-product]')?.querySelector('[data-km-compare-price]');
      const submit = form.querySelector('[data-km-add-to-cart]');
      const quantityInput = form.querySelector('input[name="quantity"]');
      if (!json || !idInput || !submit) return;

      let variants = [];
      try {
        variants = JSON.parse(json.textContent);
      } catch (_error) {
        return;
      }

      const moneyFormat = form.dataset.moneyFormat || '';
      const currency = form.dataset.currency || 'USD';
      const campaignsJson = form.querySelector('[data-km-discount-campaigns]');
      let campaignsText = campaignsJson?.textContent || '';
      try {
        campaignsText = JSON.parse(campaignsText);
      } catch (_error) {}
      const campaigns = parseDiscountCampaigns(campaignsText);
      const requestedCode = getUrlDiscountCode();
      const sessionCode = getShopifyDiscountCode();
      const defaultCode = (form.dataset.kmDiscountCode || '').trim();
      const activeCampaign = campaigns.find((campaign) => campaign.code.toLowerCase() === requestedCode.toLowerCase())
        || campaigns.find((campaign) => campaign.code.toLowerCase() === sessionCode.toLowerCase())
        || campaigns.find((campaign) => campaign.code.toLowerCase() === defaultCode.toLowerCase())
        || null;
      applyDiscountSession(activeCampaign);

      const updateSubmit = (variant) => {
        submit.disabled = !variant.available;
        if (variant.available) {
          submit.textContent = submit.dataset.addLabel;
        } else {
          submit.textContent = submit.dataset.soldOutLabel;
        }
      };

      const applyVariantRadio = (radio) => {
        if (!radio) return;
        const variant = variants.find((item) => String(item.id) === String(radio.value));
        if (!variant) return;

        idInput.value = variant.id;
        const priceDisplay = (radio.dataset.kmVariantPriceDisplay || '').trim();
        const compareDisplay = (radio.dataset.kmVariantCompareDisplay || '').trim();
        const showDisplayCompare = radio.dataset.kmVariantShowCompare === 'true' && compareDisplay;
        if (price) price.textContent = priceDisplay || formatMoney(variant.price, moneyFormat, currency);

        if (compare) {
          const showCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
          compare.hidden = !(showDisplayCompare || showCompare);
          compare.textContent = showDisplayCompare
            ? compareDisplay
            : (showCompare ? formatMoney(variant.compare_at_price, moneyFormat, currency) : '');
        }

        updateSubmit(variant);
      };

      const applyDiscountCampaign = (campaign) => {
        if (!campaign) return;
        const productRoot = form.closest('[data-km-product]');
        const discountCode = campaign.code;
        const discountMultiplier = (100 - campaign.percent) / 100;

        form.dataset.kmDiscountCode = discountCode;

        productRoot?.querySelectorAll('[data-km-discount-title]').forEach((element) => {
          element.textContent = campaign.title;
        });
        productRoot?.querySelectorAll('[data-km-discount-subtext]').forEach((element) => {
          element.textContent = campaign.subtext || `Use code ${discountCode}`;
        });
        productRoot?.querySelectorAll('[data-km-discount-deadline]').forEach((element) => {
          element.textContent = campaign.deadline;
          element.hidden = !campaign.deadline;
        });
        productRoot?.querySelectorAll(SELECTORS.copyCode).forEach((button) => {
          button.dataset.kmCopyCode = discountCode;
          button.setAttribute('aria-label', `Copy discount code ${discountCode}`);
          if (/^copy/i.test(button.textContent.trim())) button.textContent = `Copy ${discountCode}`;
        });

        form.querySelectorAll('[data-km-variant]').forEach((radio) => {
          const variant = variants.find((item) => String(item.id) === String(radio.value));
          if (!variant) return;

          const discountedPrice = Math.round((Number(variant.price) || 0) * discountMultiplier);
          const comparePrice = Number(variant.compare_at_price) > Number(variant.price)
            ? Number(variant.compare_at_price)
            : Number(variant.price);
          const showCompare = campaign.percent > 0 && comparePrice > discountedPrice;
          const priceDisplay = formatMoney(discountedPrice, moneyFormat, currency);
          const compareDisplay = showCompare ? formatMoney(comparePrice, moneyFormat, currency) : '';

          radio.dataset.kmVariantPriceDisplay = priceDisplay;
          radio.dataset.kmVariantCompareDisplay = compareDisplay;
          radio.dataset.kmVariantShowCompare = String(showCompare);

          const priceWrap = radio.nextElementSibling?.querySelector('.km-variant-pill__prices');
          if (priceWrap) {
            const sale = priceWrap.querySelector('span');
            const compareElement = priceWrap.querySelector('s');
            if (sale) sale.textContent = priceDisplay;
            if (compareElement) {
              compareElement.textContent = compareDisplay;
              compareElement.hidden = !showCompare;
            } else if (showCompare) {
              const newCompare = document.createElement('s');
              newCompare.textContent = compareDisplay;
              priceWrap.append(newCompare);
            }
          }
        });

        form.querySelectorAll('[data-km-checkout-link]').forEach((link) => {
          link.href = `/checkout?discount=${encodeURIComponent(discountCode)}`;
        });

        const selectedRadio = form.querySelector('[data-km-variant]:checked');
        if (selectedRadio) applyVariantRadio(selectedRadio);
      };

      applyDiscountCampaign(activeCampaign);

      form.querySelectorAll('[data-km-variant]').forEach((radio) => {
        radio.addEventListener('change', () => {
          if (!radio.checked) return;
          applyVariantRadio(radio);
        });
      });

      const sizeChoices = Array.from(form.querySelectorAll('[data-km-size-choice]'));
      const sizeBundleRadios = Array.from(form.querySelectorAll('[data-km-size-bundle-variant]'));
      const syncSizeBundleOptions = () => {
        if (!sizeChoices.length || !sizeBundleRadios.length) return;

        const selectedSize = sizeChoices.find((radio) => radio.checked)?.value || sizeChoices[0]?.value;
        const checkedBundle = sizeBundleRadios.find((radio) => radio.checked)?.dataset.kmBundle;
        let targetRadio = null;

        sizeBundleRadios.forEach((radio) => {
          const option = radio.closest('[data-km-size-bundle-option]');
          const isActiveSize = radio.dataset.kmSize === selectedSize;
          if (option) option.hidden = !isActiveSize;
          if (!isActiveSize) {
            radio.checked = false;
            return;
          }
          if (!targetRadio && !radio.disabled && radio.dataset.kmBundle === checkedBundle) {
            targetRadio = radio;
          }
        });

        if (!targetRadio) {
          targetRadio = sizeBundleRadios.find((radio) => radio.dataset.kmSize === selectedSize && !radio.disabled) || null;
        }

        if (targetRadio) {
          targetRadio.checked = true;
          applyVariantRadio(targetRadio);
        }
      };

      sizeChoices.forEach((radio) => {
        radio.addEventListener('change', syncSizeBundleOptions);
      });
      syncSizeBundleOptions();

      form.querySelectorAll('[data-km-pack]').forEach((radio) => {
        const updatePack = () => {
          if (!radio.checked) return;
          const quantity = Math.max(parseInt(radio.value, 10) || 1, 1);
          const variant = variants.find((item) => String(item.id) === String(idInput.value)) || variants[0];
          const packPrice = parseInt(radio.dataset.kmPackPrice, 10);
          const fallbackPrice = Number.isFinite(packPrice) ? packPrice : (variant?.price || 0) * quantity;
          const priceDisplay = (radio.dataset.kmPackPriceDisplay || '').trim();
          const compareDisplay = (radio.dataset.kmPackCompareDisplay || '').trim();
          const showCompare = radio.dataset.kmPackShowCompare === 'true' && compareDisplay;
          if (quantityInput) {
            quantityInput.value = String(quantity);
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          if (price) price.textContent = priceDisplay || formatMoney(fallbackPrice, moneyFormat, currency);
          if (compare) {
            compare.hidden = !showCompare;
            compare.textContent = showCompare ? compareDisplay : '';
          }
          if (variant) updateSubmit(variant, fallbackPrice);
        };
        radio.addEventListener('change', updatePack);
        if (radio.checked) updatePack();
      });

      form.addEventListener('submit', async (event) => {
        if (submit.disabled) return;
        if (form.dataset.kmSmartCart === 'false') return;
        if (!window.fetch || !window.FormData) return;

        event.preventDefault();
        const originalText = submit.textContent;
        const feedback = form.querySelector('[data-km-cart-feedback]');
        const feedbackTitle = form.querySelector('[data-km-cart-feedback-title]');
        const checkoutLink = form.querySelector('[data-km-checkout-link]');
        const removeButton = form.querySelector('[data-km-cart-remove]');
        const discountCode = (form.dataset.kmDiscountCode || '').trim();
        const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
        const cartUpdateUrl = window.Theme?.routes?.cart_update_url || '/cart/update.js';
        const cartChangeUrl = window.Theme?.routes?.cart_change_url || '/cart/change.js';
        const requestedQuantity = Math.max(parseInt(quantityInput?.value, 10) || 1, 1);

        const updateHeaderCount = (itemCount) => {
          document.querySelectorAll('.km-cart-count').forEach((count) => {
            count.textContent = itemCount;
            count.hidden = itemCount < 1;
          });
          document.querySelectorAll('.km-header-actions a[href$="/cart"]').forEach((cartLink) => {
            if (cartLink.querySelector('.km-cart-count')) return;
            const count = document.createElement('span');
            count.className = 'km-cart-count';
            count.setAttribute('aria-hidden', 'true');
            count.textContent = itemCount;
            count.hidden = itemCount < 1;
            cartLink.append(count);
          });
        };

        const updateFeedbackCount = (itemCount) => {
          if (!feedback) return;
          const displayCount = Math.max(Number(itemCount) || 0, 0);
          const itemText = displayCount === 1 ? '1 item' : `${displayCount} items`;
          feedback.querySelectorAll('[data-km-cart-feedback-count]').forEach((count) => {
            count.textContent = itemText;
          });
          feedback.querySelectorAll('[data-km-cart-feedback-count-short]').forEach((count) => {
            count.textContent = displayCount ? `(${displayCount})` : '';
          });
        };

        const refreshCartCount = async () => {
          const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          if (!cartResponse.ok) return null;
          const cart = await cartResponse.json();
          const cartItemCount = Number(cart.item_count) || 0;
          updateHeaderCount(cartItemCount);
          updateFeedbackCount(cartItemCount);
          return cartItemCount;
        };

        submit.disabled = true;
        submit.textContent = 'Adding...';

        try {
          const addResponse = await fetch(cartAddUrl, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
          });

          if (!addResponse.ok) throw new Error('Unable to add to cart');
          const addedItem = await addResponse.json();
          const addedLineId = addedItem?.key || addedItem?.id || idInput.value;
          const optimisticCount = Number(addedItem?.quantity) || requestedQuantity;

          if (feedbackTitle) feedbackTitle.textContent = 'Added to cart';
          if (feedback) {
            updateFeedbackCount(optimisticCount);
            feedback.hidden = false;
          }
          if (removeButton) {
            removeButton.dataset.kmCartLine = String(addedLineId || '');
            removeButton.dataset.kmCartVariant = String(addedItem?.variant_id || addedItem?.id || idInput.value || '');
            removeButton.dataset.kmCartLineQuantity = String(optimisticCount);
            removeButton.hidden = false;
            removeButton.disabled = false;
          }
          submit.disabled = false;
          submit.textContent = 'Added';
          window.setTimeout(() => {
            submit.textContent = originalText;
          }, 700);

          if (discountCode) {
            fetch(cartUpdateUrl, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ discount: discountCode }),
            }).catch(() => {});
            if (checkoutLink) checkoutLink.href = `/checkout?discount=${encodeURIComponent(discountCode)}`;
          }

          refreshCartCount().catch(() => {});
        } catch (_error) {
          submit.disabled = false;
          submit.textContent = originalText;
          form.submit();
        }

        if (removeButton && removeButton.dataset.kmReady !== 'true') {
          removeButton.dataset.kmReady = 'true';
          removeButton.addEventListener('click', async () => {
            const lineId = removeButton.dataset.kmCartLine;
            if (!lineId) return;
            const currentLineQuantity = Math.max(parseInt(removeButton.dataset.kmCartLineQuantity, 10) || 1, 1);
            const nextLineQuantity = Math.max(currentLineQuantity - 1, 0);
            const variantId = removeButton.dataset.kmCartVariant;
            removeButton.disabled = true;
            if (feedbackTitle) feedbackTitle.textContent = 'Removing...';

            try {
              const removeResponse = await fetch(cartChangeUrl, {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id: lineId, quantity: nextLineQuantity }),
              });
              if (!removeResponse.ok) throw new Error('Unable to remove item');
              const cart = await removeResponse.json();
              const cartItemCount = Number(cart.item_count) || 0;
              const lineItem = Array.isArray(cart.items)
                ? cart.items.find((item) => (
                    String(item.key) === String(lineId)
                    || String(item.id) === String(lineId)
                    || String(item.variant_id) === String(variantId)
                    || String(item.id) === String(variantId)
                  ))
                : null;
              const lineQuantity = lineItem ? Number(lineItem.quantity) || 0 : nextLineQuantity;
              updateHeaderCount(cartItemCount);
              updateFeedbackCount(cartItemCount);
              if (feedbackTitle) feedbackTitle.textContent = lineQuantity > 0 ? 'Removed 1 item' : 'Removed from cart';
              if (lineItem?.key) removeButton.dataset.kmCartLine = String(lineItem.key);
              removeButton.dataset.kmCartLineQuantity = String(lineQuantity);
              removeButton.hidden = lineQuantity < 1;
              if (lineQuantity < 1) removeButton.dataset.kmCartLine = '';
              removeButton.disabled = false;
            } catch (_error) {
              removeButton.disabled = false;
              if (feedbackTitle) feedbackTitle.textContent = 'Added to cart';
            }
          });
        }
      });
    });
  }

  function setupCopyCodes(root) {
    root.querySelectorAll(SELECTORS.copyCode).forEach((button) => {
      if (button.dataset.kmReady === 'true') return;
      button.dataset.kmReady = 'true';
      const originalText = button.textContent;

      button.addEventListener('click', async () => {
        const code = button.dataset.kmCopyCode;
        if (!code) return;

        try {
          await navigator.clipboard.writeText(code);
          button.textContent = 'Copied';
          window.setTimeout(() => {
            button.textContent = originalText;
          }, 1600);
        } catch (_error) {
          button.textContent = code;
        }
      });
    });
  }

  function setupLocalization(root) {
    root.querySelectorAll(SELECTORS.localization).forEach((select) => {
      if (select.dataset.kmReady === 'true') return;
      select.dataset.kmReady = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
  }

  function setupConcernResults(root) {
    root.querySelectorAll(SELECTORS.concernResults).forEach((section) => {
      if (section.dataset.kmReady === 'true') return;
      section.dataset.kmReady = 'true';

      const tabs = Array.from(section.querySelectorAll('[data-km-concern-tab]'));
      const panels = Array.from(section.querySelectorAll('[data-km-concern-panel]'));
      if (!tabs.length || !panels.length) return;

      const activate = (tab) => {
        tabs.forEach((item) => {
          const isActive = item === tab;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-selected', String(isActive));
          item.tabIndex = isActive ? 0 : -1;
        });

        panels.forEach((panel) => {
          const isActive = panel.id === tab.getAttribute('aria-controls');
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });
      };

      tabs.forEach((tab, index) => {
        tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const nextIndex = (index + direction + tabs.length) % tabs.length;
          tabs[nextIndex].focus();
          activate(tabs[nextIndex]);
        });
      });
    });
  }

  function setupRoutineVideos(root) {
    root.querySelectorAll(SELECTORS.routineMedia).forEach((media) => {
      if (media.dataset.kmVideoReady === 'true') return;
      if (media.matches(SELECTORS.cinematicPlayer)) return;
      const video = media.querySelector('video');
      if (!video) return;

      media.dataset.kmVideoReady = 'true';
      video.controls = true;
      video.playsInline = true;
      video.preload = video.preload === 'none' ? 'metadata' : video.preload;

      media.addEventListener('click', (event) => {
        event.preventDefault();
        video.controls = true;
        if (video.readyState === 0) video.load();

        if (video.paused) {
          const playRequest = video.play();
          if (playRequest && typeof playRequest.catch === 'function') {
            playRequest.catch(() => {
              video.controls = true;
            });
          }
        } else {
          video.pause();
        }
      });
    });
  }

  function setupVideoReviewPlayers(root) {
    root.querySelectorAll(SELECTORS.cinematicPlayer).forEach((media) => {
      if (media.dataset.kmVideoReviewReady === 'true') return;
      const video = media.querySelector('video');
      const playButton = media.querySelector('[data-km-video-review-play]');
      const skipButtons = Array.from(media.querySelectorAll('[data-km-video-review-skip]'));
      if (!video || !playButton) return;

      media.dataset.kmVideoReviewReady = 'true';
      video.playsInline = true;
      video.preload = video.preload === 'none' ? 'metadata' : video.preload;

      const syncButton = () => {
        const isPlaying = !video.paused && !video.ended;
        media.classList.toggle('is-playing', isPlaying);
        playButton.classList.toggle('is-playing', isPlaying);
        playButton.setAttribute('aria-pressed', String(isPlaying));
        playButton.setAttribute('aria-label', isPlaying ? (playButton.dataset.pauseLabel || 'Pause video') : (playButton.dataset.playLabel || 'Play video'));
      };

      const toggleVideo = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        if (video.readyState === 0) video.load();

        if (video.paused || video.ended) {
          document.querySelectorAll(`${SELECTORS.cinematicPlayer} video`).forEach((otherVideo) => {
            if (otherVideo !== video) otherVideo.pause();
          });
          const playRequest = video.play();
          if (playRequest && typeof playRequest.catch === 'function') playRequest.catch(syncButton);
        } else {
          video.pause();
        }
      };

      playButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        toggleVideo(event);
      });

      skipButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const seconds = parseInt(button.dataset.kmVideoReviewSkip, 10) || 0;
          if (!Number.isFinite(video.duration)) return;
          video.currentTime = Math.min(Math.max(video.currentTime + seconds, 0), video.duration);
        });
      });

      media.addEventListener('click', (event) => {
        if (event.target.closest('button')) return;
        toggleVideo(event);
      });

      video.addEventListener('play', syncButton);
      video.addEventListener('pause', syncButton);
      video.addEventListener('ended', syncButton);
      syncButton();
    });
  }

  function setupBottledStoryVideos(root = document) {
    root.querySelectorAll('[data-km-bottled-media]').forEach((media) => {
      if (media.dataset.kmBottledReady === 'true') return;
      const video = media.querySelector('video');
      const playButton = media.querySelector('[data-km-bottled-play]');
      const muteButton = media.querySelector('[data-km-bottled-mute]');
      if (!video || !playButton || !muteButton) return;

      media.dataset.kmBottledReady = 'true';
      video.playsInline = true;

      const syncState = () => {
        const isPlaying = !video.paused && !video.ended;
        media.classList.toggle('is-playing', isPlaying);
        media.classList.toggle('is-muted', video.muted);
        playButton.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
        muteButton.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        muteButton.setAttribute('aria-pressed', String(video.muted));
      };

      playButton.addEventListener('click', () => {
        if (video.paused || video.ended) video.play().catch(() => {});
        else video.pause();
      });
      muteButton.addEventListener('click', () => {
        video.muted = !video.muted;
        syncState();
      });
      ['play', 'pause', 'ended', 'volumechange'].forEach((eventName) => video.addEventListener(eventName, syncState));
      syncState();
    });
  }

  function initialize(root = document) {
    setupDrawer(root);
    setupCarousels(root);
    setupStarReviews(root);
    setupProductMediaCarousels(root);
    setupFaq(root);
    setupQuantity(root);
    setupProductForms(root);
    setupMobileBuyBars(root);
    setupCopyCodes(root);
    setupRoutineVideos(root);
    setupVideoReviewPlayers(root);
    setupBottledStoryVideos(root);
    setupConcernResults(root);
    setupLocalization(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
